import locationFromRequest from "#shared/utils/location";
import createAuthorizationToken from "#shared/utils/token";

import * as Sentry from "@sentry/nuxt";
import axios from "axios";
import axiosRetry from 'axios-retry'

// Create singleton axios instance with retry configuration
const createAxiosInstance = () => {
  const instance = axios.create();
  
  axiosRetry(instance, {
    retries: 5,
    retryDelay: (retryCount) => {
      return retryCount * 1000;
    },
    retryCondition: error => error.code !== 'ECONNABORTED' && !error.response
  });
  
  return instance;
};

const axiosInstance = createAxiosInstance();

import type {
  Cancel,
  CombinedField,
  Confirm,
  Customer,
  CustomerPass,
  DateObject,
  Employee,
  Error,
  Form,
  GiftCard,
  Integration,
  Item,
  Location,
  Move,
  Order,
  Pass,
  Payment,
  Rebook,
  Reservation,
  Review,
  Service,
  Waitlist,
} from "@@/types/api";

export default class ApiController {
  resource: string = "";
  // toast = useToast();
  $auth = useNuxtApp().$auth;
  $site = useNuxtApp().$site;

  router = useRouter();

  async request<T>(request: ApiRequest) {
    const defaultPort = process.env.PORT || 3000;
    const defaultHost = "127.0.0.1";

    const origin = process.browser ? '' : `http://${defaultHost}:${defaultPort}`;

    let url = origin + "/api/location" + this.resource + request.url;

    if (request.query) {
      url += "?" + new URLSearchParams(request.query).toString();
    }

    const cookie = useRequestHeaders().cookie;
    const { $auth } = useNuxtApp();
    const authStore = useAuthStore();
    const connect = undefined;
    const customer = authStore.token;

    const host = useRequestURL().host;
    const location = locationFromRequest(host);
    const token = createAuthorizationToken(
      location,
      connect,
      customer
    );

    let client = 'bookapp:site'

    const layoutStore = useLayoutStore()

    if (layoutStore?.layout === 'widget') {
      client = 'bookapp:widget'
    } else if (layoutStore?.layout === 'bot') {
      client = 'bookapp:bot'
    }
    
    const config = {
      method: request.method,
      url: url,
      headers: {
        Cookie: cookie,
        Authorization: `Bearer ${token}`,
        'x-connect-client': client,
        accept: 'application/json',
      },
      data: request.body ? request.body : undefined
    };

    try {
      const response = await axiosInstance(config);
      const responseData = response.data;

      if (responseData?.errors && !Array.isArray(responseData.errors)) {
        const errors = [];
        for (const attribute in responseData.errors) {
          errors.push(...responseData.errors[attribute]);
        }
        responseData.errors = errors;
      }

      return responseData as ApiResponse<T>;
    } catch (error: any) {
      const exception = new ApiException(error);

      switch (exception.status) {
        case 401:
          // console.log("authentication error");

          $auth?.logout()

          this.router.push('/auth/login')

          throw exception;

        case 403:
          // console.log("permission error");

          throw exception;

        case 404:
          // console.log(exception);

          break;

        default:
          const description = exception.errors
            ? Object.values(exception.errors).join("\n")
            : "An error occurred. Please try again.";

          // console.log(error);

          throw exception;
      }
    }
  }
}

export class ApiRequest {
  public method: "POST" | "GET" | "PUT" | "DELETE" | "PATCH";
  public url: string;
  public body?: object;
  public query?: Record<string, string>;

  constructor({
    method,
    url,
    body,
    query,
  }: {
    method: "POST" | "GET" | "PUT" | "DELETE" | "PATCH";
    url: string;
    body?: object;
    query?: Record<string, string>;
  }) {
    this.method = method;
    this.url = url;
    this.body = body;
    this.query = query;
  }
}

export class ApiException {
  public type: string;
  public status: number;
  public data: any;

  constructor(exception: any) {
    this.type = exception.response?.type || exception.response?.statusText;
    this.status = exception.response?.status;
    this.data = exception.response?.data;
  }

  get errors() {
    return this.data?.errors || false;
  }
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  errors: any[];
  error?: Error;
  paging?: ApiPaging;
  cancel?: Cancel;
  canFillOut?: boolean;
  confirm?: Confirm;
  consentAcceptLabel?: string;
  consentContent?: string;
  consentRequired?: boolean;
  consentTitle?: boolean;
  customer?: Customer;
  customerPass?: CustomerPass;
  customers?: Customer[];
  dates?: DateObject[];
  employees?: Employee[];
  fields?: CombinedField[];
  form?: Form;
  forms?: Form[];
  found?: boolean;
  giftCard?: GiftCard;
  giftCards?: GiftCard[];
  integrations?: Integration[];
  item?: Item;
  marketingConsentAcceptLabel?: string;
  marketingConsentContent?: string;
  marketingConsentRequired?: boolean;
  marketingConsentTitle?: string;
  move?: Move;
  order?: Order;
  orders?: Order[];
  payment?: Payment;
  passes?: Pass[];
  rebook?: Rebook;
  reservation?: Reservation;
  reservations?: Reservation[];
  review?: Review;
  services?: Service[];
  waitlist?: Waitlist;
  waitlists?: Waitlist[];
}

export interface ApiPaging {
  page: number;
  limit: number;
  itemCount: number;
  pageCount: number;
}
